import React, { useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { HELP_DESK_STATUSES, DEFAULT_RESOLUTION_TIMEOUT_HOURS } from '@ubeya/shared/constants/helpDesk';
import useHelpDeskAccountSettings from '@ubeya/shared/hooks/account/useHelpDeskAccountSettings';
import useHelpDeskUpdateTicket from '@ubeya/shared/hooks/account/useHelpDeskUpdateTicket';
import useToaster from '@ubeya/shared/hooks/useToaster';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as SnoozeIcon } from '@ubeya/shared-web/assets/help-desk/snooze.svg';
import { ReactComponent as BaseArchiveIcon } from '@ubeya/shared-web/assets/archive.svg';
import { ReactComponent as BaseAutoCloseIcon } from '@ubeya/shared-web/assets/help-desk/auto-close.svg';
import { useHelpDeskAnalytics } from '../../helpDeskAnalytics';
import { getSnoozedLabel, isTicketResolvedPending } from '../../helpDeskUtils';

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 9px 16px;
  min-width: 0;
`;

const SnoozeBanner = styled(Banner)`
  background: ${({ theme }) => theme.colors.blueLight};
  color: ${({ theme }) => theme.colors.black};
`;

const ClosedBanner = styled(Banner)`
  background: ${({ theme }) => theme.colors.disabled};
`;

const ResolvedPendingBanner = styled(Banner)`
  background: ${({ theme }) => theme.colors.disabled};
`;

const BannerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ArchiveIcon = styled(BaseArchiveIcon)`
  path {
    fill: ${({ theme }) => theme.colors.black};
  }
`;

const AutoCloseIcon = styled(BaseAutoCloseIcon)`
  color: ${({ theme }) => theme.colors.black};
`;

const BannerText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BannerAction = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  max-width: 40%;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const AutoCloseBanner = ({ ticket, accountId }) => {
  const { t, timeFormat, dateFormat } = useContext(ConfigContext);
  const { settings } = useHelpDeskAccountSettings({ accountId });
  const isResolvedPending = isTicketResolvedPending(ticket);

  const label = useMemo(() => {
    if (!isResolvedPending) return null;
    const resolutionTimeoutHours = settings?.resolutionTimeoutHours ?? DEFAULT_RESOLUTION_TIMEOUT_HOURS;
    const autoCloseUtc = ticket.resolvedAutoHandlerTime
      ? moment.utc(ticket.resolvedAutoHandlerTime)
      : moment.utc(ticket.resolvedAt).add(resolutionTimeoutHours, 'hours');
    if (!autoCloseUtc.isValid()) return null;
    const autoCloseMoment = autoCloseUtc.local();
    const timeStr = autoCloseMoment.format(`${timeFormat} ${dateFormat}`);
    const diffHours = Math.round(autoCloseMoment.diff(moment()) / (1000 * 60 * 60));
    const duration =
      diffHours < 1 ? t('help_desk_less_than_one_hour_from_now') : t('help_desk_hours_from_now', { value: diffHours });
    return t('help_desk_auto_close_banner', { duration, time: timeStr });
  }, [
    isResolvedPending,
    settings?.resolutionTimeoutHours,
    ticket.resolvedAutoHandlerTime,
    ticket.resolvedAt,
    t,
    timeFormat,
    dateFormat
  ]);

  if (!label) return null;

  return (
    <ResolvedPendingBanner>
      <BannerIcon>
        <AutoCloseIcon />
      </BannerIcon>
      <BannerText>{label}</BannerText>
    </ResolvedPendingBanner>
  );
};

const HelpDeskTicketStatusBanners = ({ ticket, accountId, snoozedUntilOverride }) => {
  const { t } = useContext(ConfigContext);
  const { addSuccess, addResponseError } = useToaster();
  const { trackHelpDeskTicketEvent } = useHelpDeskAnalytics();
  const { updateTicket } = useHelpDeskUpdateTicket({ accountId, ticketId: ticket?.id });

  const isSnoozed = ticket?.status === HELP_DESK_STATUSES.SNOOZED;
  const isClosed = ticket?.status === HELP_DESK_STATUSES.CLOSED;

  const snoozeLabel = useMemo(() => {
    if (!isSnoozed) return null;
    const snoozedUntilValue = snoozedUntilOverride || ticket?.snoozedUntil;
    return getSnoozedLabel(snoozedUntilValue, t) || t('help_desk_snoozed');
  }, [isSnoozed, snoozedUntilOverride, ticket?.snoozedUntil, t]);

  const handleUnsnooze = useCallback(() => {
    updateTicket(
      { status: HELP_DESK_STATUSES.OPEN, snoozedUntil: null },
      {
        onSuccess: () => {
          trackHelpDeskTicketEvent({
            eventName: 'Help Desk - Admin Unsnoozed Ticket',
            context: 'Help Desk ticket status banner',
            ticket,
            snoozedUntil: null
          });
          addSuccess(t('help_desk_ticket_unsnoozed'));
        },
        onError: addResponseError
      }
    );
  }, [addSuccess, addResponseError, t, ticket, trackHelpDeskTicketEvent, updateTicket]);

  const handleReopen = useCallback(() => {
    updateTicket(
      { status: HELP_DESK_STATUSES.OPEN, snoozedUntil: null },
      {
        onSuccess: () => {
          trackHelpDeskTicketEvent({
            eventName: 'Help Desk - Admin Reopened Ticket',
            context: 'Help Desk ticket status banner',
            ticket,
            ticketStatus: HELP_DESK_STATUSES.OPEN
          });
          addSuccess(t('help_desk_ticket_reopened'));
        },
        onError: addResponseError
      }
    );
  }, [addSuccess, addResponseError, t, ticket, trackHelpDeskTicketEvent, updateTicket]);

  return (
    <>
      {snoozeLabel && (
        <SnoozeBanner>
          <BannerIcon>
            <SnoozeIcon />
          </BannerIcon>
          <BannerText>{snoozeLabel}</BannerText>
          <BannerAction onClick={handleUnsnooze}>{t('help_desk_unsnooze')}</BannerAction>
        </SnoozeBanner>
      )}

      {isClosed && (
        <ClosedBanner>
          <BannerIcon>
            <ArchiveIcon />
          </BannerIcon>
          <BannerText>{t('help_desk_ticket_closed_banner')}</BannerText>
          <BannerAction onClick={handleReopen}>{t('help_desk_reopen')}</BannerAction>
        </ClosedBanner>
      )}

      <AutoCloseBanner ticket={ticket} accountId={accountId} />
    </>
  );
};

export default HelpDeskTicketStatusBanners;
